// frontend/src/index.js
// Entry point for the React app—loads Tailwind, configures Axios (base URL,
// auth/refresh interceptors), restores session, and renders <App /> inside Redux <Provider>.

import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import { Provider } from 'react-redux';

import App from './App';
import store from './store';

import './index.css';

// Axios global defaults
axios.defaults.baseURL =
  process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
axios.defaults.withCredentials = true;

// Attach Authorization header using stored access token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (err) => Promise.reject(err)
);

// Prime session on app startup
const existingToken = localStorage.getItem('accessToken');
if (existingToken) {
  axios.defaults.headers.common.Authorization = `Bearer ${existingToken}`;
}

// Response interceptor: handle 401 by attempting a refresh for non-auth endpoints
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.error(
      'Got HTTP',
      error.response?.status,
      'from',
      error.config?.method?.toUpperCase(),
      error.config?.url
    );

    const originalRequest = error.config;
    const status = error.response?.status;
    const url = originalRequest?.url || '';

    const isLogin = url.endsWith('/auth/login');
    const isRefresh = url.endsWith('/auth/refresh');
    const isWhoami = url.endsWith('/auth/me');

    if (
      status === 401 &&
      !originalRequest._retry &&
      !isLogin &&
      !isRefresh &&
      !isWhoami
    ) {
      originalRequest._retry = true;
      try {
        const { data } = await axios.post('/auth/refresh');
        const { accessToken } = data;

        localStorage.setItem('accessToken', accessToken);
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        return axios(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

ReactDOM.createRoot(document.getElementById('root')).render(
  <Provider store={store}>
    <App />
  </Provider>
);
